import { transformAllPages } from 'collections/book-creation/helper';
import useEntityActionCreators from 'entities/hooks/use-entity-action-creators';
import normalize from 'jsonapi-normalizer';
import React, { createContext, useContext, useState } from 'react';
import useSWR from 'swr';
import { getSWR } from 'utils/api';

// States returned by the API
export const BOOK_PAGES_STATES = {
  WAITING_FOR_MEDIA: 'waiting_for_media',
  CREATING_PAGES: 'creating_pages',
  FINISHED: 'finished_creating_pages',
};

const POLLING_STATES = new Set([
  BOOK_PAGES_STATES.WAITING_FOR_MEDIA,
  BOOK_PAGES_STATES.CREATING_PAGES,
]);

const BookPagesContext = createContext(null);

// Pure state container — no fetching here.
// Fetching is initialized by calling useInitBookPages() in the nav component.
export const BookPagesProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [totalPagesData, setTotalPagesData] = useState([]);

  return (
    <BookPagesContext.Provider
      value={{
        state,
        setState,
        pages,
        setPages,
        isLoading,
        setIsLoading,
        error,
        setError,
        currentPage,
        setCurrentPage,
        selectedPageIndex,
        setSelectedPageIndex,
        totalPagesData,
        setTotalPagesData,
      }}
    >
      {children}
    </BookPagesContext.Provider>
  );
};

export const useBookPages = () => {
  const ctx = useContext(BookPagesContext);
  if (!ctx) throw new Error('useBookPages must be used within BookPagesProvider');
  return ctx;
};

// Call this in the component that should initialize fetching (story-detail-header-nav).
// It writes API results directly into BookPagesContext.
export const useInitBookPages = () => {
  const { setState, setPages, setIsLoading, setError, state } = useBookPages();
  const { receive } = useEntityActionCreators();
  const [storyId, setStoryId] = useState(false);

  // Process the response in SWR's onSuccess (fires on EVERY successful request),
  // instead of a useEffect on `data` — SWR keeps the same `data` reference when a
  // poll returns identical content, so a data-dependency effect would be skipped.
  const handleBookPages = (payload) => {
    // Mirror the global JSON:API onSuccess (json-api-client) so page layers land in
    // the store — a hook-level onSuccess overrides the global one.
    if (payload && payload.data) {
      receive({ payload: { ...payload, data: normalize(payload.data) } });
    }
    if (payload?.status === 200) {
      setState(payload?.data?.meta?.state_for_page_generation ?? null);
      setPages(payload?.data ? transformAllPages(payload.data) : []);
      setIsLoading(false);
      setError(null);
    }
  };

  useSWR(
    storyId ? `/v3/stories/${storyId}/book_pages` : null,
    getSWR,
    {
      refreshInterval: POLLING_STATES.has(state) ? 3000 : 0,
      revalidateOnFocus: false,
      onLoadingSlow: () => setIsLoading(true),
      onSuccess: handleBookPages,
    }
  );

  const refreshBookPages = (id) => {
    setState(BOOK_PAGES_STATES.WAITING_FOR_MEDIA)
    setIsLoading(true)
    setStoryId(id);
  };

  return {
    refreshBookPages
  }
};
